/** @file Flattens a tree into the visible rows for the current expanded set. */

/** One node of the data a Tree shows; `label` is data (an entity name), not a UI string. */
export interface TreeNode {
  id: string;
  label: string;
  /** Short symbol drawn before the label (kind marker); data, never translated, never renamed. */
  badge?: string;
  children?: readonly TreeNode[];
}

/** One visible row: a node with its depth and open state. */
export interface TreeRow {
  id: string;
  label: string;
  badge?: string;
  depth: number;
  parentId: string | null;
  hasChildren: boolean;
  expanded: boolean;
}

interface Frame {
  node: TreeNode;
  depth: number;
  parentId: string | null;
}

/** Pushes `nodes` so that popping the stack yields them in order. */
function pushReversed(stack: Frame[], nodes: readonly TreeNode[], depth: number, parentId: string | null): void {
  for (let i = nodes.length - 1; i >= 0; i -= 1) {
    const node = nodes[i];
    if (node) stack.push({ node, depth, parentId });
  }
}

/** Depth-first rows of `nodes`; a node's children appear only when its id is in `expanded`. */
export function flattenTree(nodes: readonly TreeNode[], expanded: ReadonlySet<string>): TreeRow[] {
  const rows: TreeRow[] = [];
  const stack: Frame[] = [];
  pushReversed(stack, nodes, 0, null);
  for (let frame = stack.pop(); frame; frame = stack.pop()) {
    const { node, depth, parentId } = frame;
    const children = node.children ?? [];
    const open = children.length > 0 && expanded.has(node.id);
    const badge = node.badge === undefined ? {} : { badge: node.badge };
    rows.push({ id: node.id, label: node.label, ...badge, depth, parentId, hasChildren: children.length > 0, expanded: open });
    if (open) pushReversed(stack, children, depth + 1, node.id);
  }
  return rows;
}
